/**
 * SignatureCapture - On-device digital signature collection.
 *
 * Uses the signature_pad library to render a canvas-based signature pad.
 * The captured signature is exported as a PNG base64 data URL.
 *
 * Features:
 * - Responsive canvas that fills its container width
 * - Touch and mouse support (handled natively by signature_pad)
 * - Clear button to reset the canvas
 * - "Accept" button to confirm and export the signature
 * - Displays a captured signature as a static image once accepted
 * - Can be toggled on/off per work order (signature_required flag)
 * - "Edit" button to re-open the pad after accepting
 *
 * @example
 * <SignatureCapture
 *	 onSave={handleSignatureSave}
 *	 label="Customer Signature"
 * />
 *
 * @example
 * <SignatureCapture
 *	 onSave={handleSignatureSave}
 *	 existingSignature={workOrder.signatureDataUrl}
 *	 disabled={true}
 * />
 */
import React, { useRef, useState, useEffect, useCallback } from 'react';
import SignaturePad from 'signature_pad';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

/**
 * Props for the SignatureCapture component.
 *
 * @property onSave				- Callback fired with the base64 PNG data URL when the user accepts
 * @property existingSignature	- If provided, displays this signature as a read-only image
 * @property disabled			- When true, disables all interactive controls
 * @property label				- Label text displayed above the signature pad (default "Signature")
 */
export interface SignatureCaptureProps {
	onSave: (signatureData: string) => void;
	existingSignature?: string;
	disabled?: boolean;
	label?: string;
	/** Optional outer className merged onto the root element. */
	className?: string;
}

/* ============================================
 * COMPONENT
 * ============================================ */

const SignatureCapture: React.FC<SignatureCaptureProps> = ({
	onSave,
	existingSignature,
	disabled = false,
	label = 'Signature',
	className,
}) => {
	/* Ref to the <canvas> DOM element where signature_pad renders */
	const canvasRef = useRef<HTMLCanvasElement>(null);

	/* Ref to the container div — used to measure width for responsive canvas sizing */
	const containerRef = useRef<HTMLDivElement>(null);

	/* Ref to the SignaturePad instance — persists across renders without re-initialization */
	const sigPadRef = useRef<SignaturePad | null>(null);

	/* Whether the user has accepted a signature (shows static image instead of pad) */
	const [isAccepted, setIsAccepted] = useState(!!existingSignature);

	/* The accepted signature as a base64 PNG data URL */
	const [signatureDataUrl, setSignatureDataUrl] = useState<string | null>(
		existingSignature || null
	);

	/* Whether the signature pad canvas is currently empty */
	const [isEmpty, setIsEmpty] = useState(true);

	/**
	 * Initializes the SignaturePad instance when the canvas is available and the pad
	 * is in drawing mode (not accepted / not showing existing signature).
	 *
	 * Also handles responsive resizing: the canvas pixel dimensions are set to match
	 * the container width at the device pixel ratio for crisp rendering on Retina displays.
	 */
	useEffect(() => {
		/* Skip initialization when showing a static accepted signature */
		if (isAccepted || disabled) return;

		const canvas = canvasRef.current;
		const container = containerRef.current;
		if (!canvas || !container) return;

		/**
		 * Resizes the canvas to match the container width.
		 * Uses devicePixelRatio for sharp rendering on high-DPI screens.
		 * Must be called on mount and whenever the container resizes.
		 */
		const resizeCanvas = () => {
			/* Get the container's computed width */
			const containerWidth = container.offsetWidth;
			/* Fixed height of 200px for the signature area */
			const canvasHeight = 200;
			/* Device pixel ratio for Retina/HiDPI sharpness */
			const ratio = window.devicePixelRatio || 1;

			/* Set the canvas display size via CSS */
			canvas.style.width = `${containerWidth}px`;
			canvas.style.height = `${canvasHeight}px`;

			/* Set the canvas buffer size (actual pixel dimensions) */
			canvas.width = containerWidth * ratio;
			canvas.height = canvasHeight * ratio;

			/* Scale the drawing context to match the pixel ratio so
			   strokes appear at the correct visual size */
			const ctx = canvas.getContext('2d');
			if (ctx) {
				ctx.scale(ratio, ratio);
			}

			/* Clear any existing signature data after resize because the
			   canvas buffer was reset. Notify the pad instance. */
			if (sigPadRef.current) {
				sigPadRef.current.clear();
				setIsEmpty(true);
			}
		};

		/* Initialize the SignaturePad with styling that matches the brand */
		const signaturePad = new SignaturePad(canvas, {
			/* Dark ink color for maximum readability */
			penColor: '#000000',
			/* Slightly thicker minimum width for touch-friendly writing */
			minWidth: 1.5,
			/* Maximum stroke width for natural-looking pressure variation */
			maxWidth: 3,
			/* Light background color to indicate the drawing area */
			backgroundColor: 'rgb(248, 249, 250)',
		});

		/* Store the instance in a ref for access from callbacks */
		sigPadRef.current = signaturePad;

		/* Track when the user starts drawing to update the isEmpty state */
		signaturePad.addEventListener('endStroke', () => {
			setIsEmpty(signaturePad.isEmpty());
		});

		/* Perform initial sizing */
		resizeCanvas();

		/* Re-size the canvas when the window resizes (e.g., orientation change on mobile) */
		window.addEventListener('resize', resizeCanvas);

		/* Cleanup: destroy the SignaturePad instance and remove the resize listener */
		return () => {
			signaturePad.off();
			window.removeEventListener('resize', resizeCanvas);
			sigPadRef.current = null;
		};
	}, [isAccepted, disabled]);

	/**
	 * Clears the signature pad canvas and resets the empty state.
	 * Called when the user clicks the "Clear" button.
	 */
	const handleClear = useCallback(() => {
		if (sigPadRef.current) {
			sigPadRef.current.clear();
			setIsEmpty(true);
		}
	}, []);

	/**
	 * Accepts the current signature by exporting it as a PNG data URL.
	 * Switches the component to the "accepted" state which shows a static image.
	 * Calls the onSave callback so the parent can store/upload the signature.
	 */
	const handleAccept = useCallback(() => {
		if (!sigPadRef.current || sigPadRef.current.isEmpty()) return;

		/* Export the canvas contents as a PNG base64 data URL */
		const dataUrl = sigPadRef.current.toDataURL('image/png');

		/* Update local state to show the accepted signature as a static image */
		setSignatureDataUrl(dataUrl);
		setIsAccepted(true);

		/* Notify the parent component with the signature data */
		onSave(dataUrl);
	}, [onSave]);

	/**
	 * Re-opens the signature pad for editing by switching back to drawing mode.
	 * Clears the previous signature so the user starts fresh.
	 */
	const handleEdit = useCallback(() => {
		setIsAccepted(false);
		setSignatureDataUrl(null);
		setIsEmpty(true);
	}, []);

	return (
		<div className={['signature-capture', 'flex flex-col gap-8px', className ?? ''].join(' ')} data-ref="organism-signature-capture">
			{/* Section label above the signature pad */}
			<label className="signature-capture__label text-14px font-sans font-semibold text-brand-text-muted">
				{label}
			</label>

			{/* Conditional rendering: show either the drawing pad or the static accepted image */}
			{isAccepted && signatureDataUrl ? (
				/* --- Accepted signature display ---
				   Shows the signature as a static image with an optional "Edit" button
				   to re-open the drawing pad. */
				<div className="flex flex-col gap-8px">
					{/* Static signature image with border and light background */}
					<div className="border border-brand-border rounded-lg overflow-hidden bg-brand-surface">
						<img src={signatureDataUrl} alt="Captured signature" className="w-full h-200px object-contain" />
					</div>

					{/* Edit button — allows the user to re-draw their signature.
						Hidden when the component is disabled (read-only mode). */}
					{!disabled && (
						<button type="button" onClick={handleEdit} className="inline-flex items-center justify-center gap-6px px-16px py-8px text-14px font-sans font-medium text-brand-primary bg-white border border-brand-primary rounded-md hover:bg-brand-primary/10 min-h-44px transition-colors self-start">
							{/* Pencil edit icon */}
							<svg className="h-16px w-16px" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
								<path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
							</svg>
							<span>Edit Signature</span>
						</button>
					)}
				</div>
			) : disabled ? (
				/* --- Disabled empty state ---
				   Shown when the component is disabled and no existing signature is provided.
				   Indicates that no signature has been captured yet. */
				<div className="border border-brand-border rounded-lg bg-brand-surface flex items-center justify-center h-200px">
					<p className="text-14px font-sans text-brand-text-muted">No signature captured</p>
				</div>
			) : (
				/* --- Drawing mode ---
				   Shows the canvas-based signature pad with Clear and Accept buttons. */
				<div ref={containerRef} className="flex flex-col gap-8px">
					{/* Canvas element — signature_pad renders strokes here.
						The border and rounded corners provide visual boundaries.
						Touch events are handled internally by the signature_pad library. */}
					<div className="border-2 border-brand-border rounded-lg overflow-hidden">
						<canvas ref={canvasRef} className="touch-none cursor-crosshair" aria-label="Signature drawing area" role="img" />
					</div>

					{/* Instruction text — helps users understand they can draw on the pad */}
					<p className="text-12px font-sans text-brand-text-subtle text-center">
						Draw your signature above using your finger or mouse
					</p>

					{/* Action buttons: Clear and Accept Signature */}
					<div className="flex items-center gap-8px">
						{/* Clear button — resets the canvas to a blank state.
							Disabled when the canvas is already empty. */}
						<button type="button" onClick={handleClear} disabled={isEmpty} className="inline-flex items-center justify-center gap-6px px-16px py-8px text-14px font-sans font-medium text-brand-text-muted bg-white border border-brand-border rounded-md hover:bg-brand-surface disabled:opacity-40 disabled:cursor-not-allowed min-h-44px transition-colors">
							{/* X circle icon */}
							<svg className="h-16px w-16px" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
								<path strokeLinecap="round" strokeLinejoin="round" d="m9.75 9.75 4.5 4.5m0-4.5-4.5 4.5M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
							</svg>
							<span>Clear</span>
						</button>

						{/* Accept button — exports the signature and switches to read-only mode.
							Disabled when the canvas is empty (nothing to accept).
							Styled with brand orange to indicate the primary action. */}
						<button type="button" onClick={handleAccept} disabled={isEmpty} className="inline-flex items-center justify-center gap-6px px-16px py-8px text-14px font-sans font-semibold text-white bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-40 disabled:cursor-not-allowed rounded-md min-h-44px transition-colors">
							{/* Checkmark icon */}
							<svg className="h-16px w-16px" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
								<path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
							</svg>
							<span>Accept Signature</span>
						</button>
					</div>
				</div>
			)}
		</div>
	);
};

export default SignatureCapture;
